import { ForbiddenError } from "@casl/ability";
import { z } from "zod";

import { AbilityService } from "@archipad-js/core/ability";
import { IllegalStateError } from "@archipad-js/core/error";

import { ArchipadAbility } from "@archipad-js/archipad/entity-services";
import { BugService, BugWhereInput } from "@archipad-js/archipad/entity-services";

import { BugEntity, BugNoteEntity, MapEntity, VisitEntity, WorkPackageEntity, PhaseEntity } from "@archipad-js/archipad-model";
import { WorkflowManager } from "@archipad/backend/project/workflowManager";
import { CreateEntityPayload } from "@archipad-js/core/orm";
import { BugGroupEntity, ProjectEntity } from "@archipad-js/archipad-model";
import { PhaseService } from "@archipad-js/archipad/entity-services";

export class BugFacade {
    constructor(
        private readonly abilityService: AbilityService<ArchipadAbility>,
        private readonly bugService: BugService,
        private readonly workflowManager: WorkflowManager,
        private readonly phaseService: PhaseService,
    ) {}

    //#region Bug

    /**
     * Creates a new bug and execute opening transition on it.
     * 
     * It will try to find a reference bug to copy some properties from it.
     */
    public openNewBug(plainPayload: CreateBugPayload): BugEntity {
        ForbiddenError.from(this.abilityService.ability).throwUnlessCan("create", "entity_Bug");

        const payload = CreateBugSchema.parse(plainPayload);

        const { visit } = payload;

        const bugGroup = visit?.visitType?.bugGroup ?? plainPayload.bugGroup;
        if (!this.workflowManager.canEnterOpenState(plainPayload.project, bugGroup, null)) {
            throw new IllegalStateError("Current user is not allowed to open a bug");
        }

        const previousBug = this.findPreviousBug({ map: payload.map, bugGroup: bugGroup, visit });
            

        if (payload.location === undefined) {
            payload.location = previousBug?.location ?? null;
        }

        if (payload.workPackage === undefined) {
            payload.workPackage = previousBug?.workPackage ?? null;
        }

        if (payload.phase === undefined) {
            payload.phase = visit?.phase ?? previousBug?.phase ?? this.phaseService.getDefaultPhaseForBugGroup(plainPayload.project, plainPayload.bugGroup);
        }

        const newBug = this.bugService.createBug(payload);
        this.workflowManager.enterOpenState(newBug);
        return newBug;
    }



    /**
     * Returns previous bug on the given map of the bug group.
     * 
     * Aim to copy some properties from it like location and work package.
     */
    private findPreviousBug(options: { map: MapEntity; bugGroup: BugGroupEntity; visit: VisitEntity }): BugEntity | undefined {
        const { map, bugGroup, visit } = options;

        const findBugWhereInput: BugWhereInput = {
            project: map.project,
            bugGroups: [bugGroup],
            maps: [map],
            visits: visit ? [visit] : undefined,
        };

        const bug = this.bugService.findBug({
            where: findBugWhereInput,
            orderBy: "index desc, creationDate desc",
        });

        return bug;
    }

    //#endregion

    //#region BugNote

    public updateText(bugNote: BugNoteEntity, text: string): void {
        ForbiddenError.from(this.abilityService.ability).throwUnlessCan("update", bugNote, "text");
        this.bugService.updateBugNote(bugNote, { text });
    }

    //#endregion
}

export const CreateBugSchema = z.object({
    map: z.instanceof(MapEntity),
    visit: z.instanceof(VisitEntity).nullish(),
    index: z.number(),
    location: z.string().nullish(),
    phase: z.instanceof(PhaseEntity).nullish(),
    positionX: z.number().optional(),
    positionY: z.number().optional(),
    temp: z.boolean().optional(),
    workPackage: z.instanceof(WorkPackageEntity).nullish(),
    project: z.instanceof(ProjectEntity),
    bugGroup: z.instanceof(BugGroupEntity).nullish(),
}).passthrough();

type CreateBugSchema = z.infer<typeof CreateBugSchema>;

export type CreateBugPayload = CreateBugSchema & CreateEntityPayload<BugEntity>;
